import type { CreateShopModalProps } from "@v2/types/modalTypes";

import React, { useState } from "react";

import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { ResponsiveDialog } from "@replohq/lattice/blocks/modal/ResponsiveDialog";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import { toast } from "@replohq/lattice/components/toast/Toast";
import {
  useCurrentWorkspace,
  useCurrentWorkspaceId,
} from "@v2/contexts/WorkspaceDashboardContext";
import {
  isAgentLaunchDismissed,
  recordAgentLaunchDismissal,
} from "@v2/features/agent-beta/agentLaunchDismissal";
import { AgentLaunchModal } from "@v2/features/agent-beta/AgentLaunchModal";
import { useOnboardingCreateProject } from "@v2/features/onboarding/hooks/useOnboardingCreateProject";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import useInstallShopify from "@v2/hooks/useInstallShopify";
import { workspaceRouter } from "@v2/trpc/routes/workspace";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate } from "react-router-dom";

export function CreateShopModal({
  workspaceId: propWorkspaceId,
  title,
  isShopifyPaymentProcessor: isShopifyPaymentProcessorProp,
  open,
  onOpenChange,
}: CreateShopModalProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const currentWorkspaceId = useCurrentWorkspaceId();
  const workspace = useCurrentWorkspace();
  const installShopify = useInstallShopify();
  const { createProject } = useOnboardingCreateProject();

  const workspaceId = propWorkspaceId ?? currentWorkspaceId;
  const isShopifyPaymentProcessor =
    isShopifyPaymentProcessorProp ?? workspace?.paymentProcessor === "shopify";
  const { data: workspaceData } = workspaceRouter.getById.useQuery(
    { id: workspaceId ?? "" },
    { enabled: Boolean(workspaceId) },
  );
  const targetWorkspace =
    workspaceData?.workspace ??
    (workspace?.id === workspaceId ? workspace : null);
  const isAgentDefault =
    targetWorkspace?.defaultProjectType === "agent" &&
    !isShopifyPaymentProcessor;

  const [shopName, setShopName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasDeclinedLetter, setHasDeclinedLetter] = useState(false);
  const [isJoiningBeta, setIsJoiningBeta] = useState(false);

  const isBetaEligible =
    Boolean(workspaceId) && !isAgentDefault && !isShopifyPaymentProcessor;
  // First-time creators see the founder letter first; declining reveals the name form.
  const isLetterOpen =
    open &&
    isBetaEligible &&
    !hasDeclinedLetter &&
    !isAgentLaunchDismissed("create-flow");
  const isCreatingAgentProject = isAgentDefault || isJoiningBeta;

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setShopName("");
      setHasDeclinedLetter(false);
      setIsJoiningBeta(false);
      onOpenChange(false);
    }
  };

  const handleContinue = async () => {
    if (isLoading || !shopName.trim() || !user || !workspaceId) {
      return;
    }
    setIsLoading(true);

    try {
      const result = await createProject({
        shopName: shopName.trim(),
        workspaceId,
        hostingType: isCreatingAgentProject ? "replo" : "shopify",
        userEmail: user.email,
        shouldInvalidateUser: true,
        isAgentEnabled: isCreatingAgentProject,
      });

      handleOpenChange(false);

      if (isCreatingAgentProject) {
        navigate(
          generatePath(routes.agent.launchpad, {
            projectId: result.project.id,
          }),
          { replace: true },
        );
      } else if (isShopifyPaymentProcessor) {
        installShopify({
          cookie: {
            type: "onboarding",
            workspaceId: result.project.ownerWorkspaceId,
            projectId: result.project.id,
            hostingType: "shopify",
          },
        });
      } else {
        navigate(`/editor/${result.project.id}/new`, { replace: true });
      }
    } catch {
      toast.error({
        title: "Couldn't create project",
        description: "Try again. If the problem continues, contact support.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isAgentNameStep = isCreatingAgentProject && !isAgentDefault;

  return (
    <>
      <AgentLaunchModal
        open={isLetterOpen}
        source="create-project"
        onEnterBeta={() => {
          setIsJoiningBeta(true);
          setHasDeclinedLetter(true);
        }}
        onDecline={() => {
          recordAgentLaunchDismissal("create-flow");
          setHasDeclinedLetter(true);
        }}
      />
      <ResponsiveDialog
        open={open && !isLetterOpen}
        onOpenChange={handleOpenChange}
        title={
          isAgentNameStep
            ? "Name your Replo Agent project"
            : title ?? "Create new project"
        }
        size="sm"
        footer={
          <Button
            type="submit"
            form="create-project-form"
            disabled={isLoading || !shopName.trim() || !workspaceId}
            loading={isLoading}
            className="w-full"
          >
            {isAgentNameStep ? "Create Agent project" : "Create new project"}
          </Button>
        }
      >
        <form
          id="create-project-form"
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void handleContinue();
          }}
        >
          <div className="flex flex-col gap-2">
            <div className="flex justify-center overflow-hidden rounded-xl bg-surface p-4">
              <img
                src="/images/chat/onboarding-hero.svg"
                alt=""
                className="h-auto w-48"
              />
            </div>
            {isBetaEligible && !isCreatingAgentProject && (
              <div className="flex flex-wrap items-center gap-x-1 text-sm text-placeholder">
                <span>Want the new Replo Agent instead?</span>
                <Button
                  type="button"
                  variant="link"
                  disabled={isLoading}
                  aria-label="Join the Replo Agent beta"
                  onClick={() => setIsJoiningBeta(true)}
                >
                  <span className="text-primary underline underline-offset-4">
                    Join the beta
                  </span>
                </Button>
              </div>
            )}
          </div>
          <LabeledControl id="project-name" label="Project name" size="base">
            <Input
              id="project-name"
              value={shopName}
              onChange={(event) => setShopName(event.target.value)}
              size="base"
              placeholder="Enter a name for your project"
              autoFocus
              required
            />
          </LabeledControl>
        </form>
      </ResponsiveDialog>
    </>
  );
}
